import { useQuery } from '@tanstack/react-query';
import {
  AlertsSearchResponse,
  CasesSearchResponse,
  EntitySearchResponse,
  EventsSearchResponse,
  FlowsSearchResponse,
  InstrumentsSearchResponse,
  RulesSearchResponse,
  SearchRequest,
  SearchResponse,
  SearchTypes,
  TeamsSearchResponse,
} from 'app/modules/search/models';
import { NAVIGATOR_QUERY_KEYS } from 'app/modules/navigator/queries/keys';
import { post } from 'app/shared/utils/fetchr';
import {
  selectSearchV2Enabled,
  selectSearchV2UseDlHashIdEnabled,
} from 'app/shared/featureFlags/selectors';
import { useSelector } from 'react-redux';
import { GenericSearchPayload } from 'app/modules/search/payload';
import {
  ActionEventSearchV2Response,
  AlertSearchV2Response,
  CaseSearchV2Response,
  EntitySearchV2Response,
  InstrumentSearchV2Response,
  NotesSearchV2Response,
  RuleSearchV2Response,
  SearchV2Response,
  TeamSearchV2Response,
  TransactionSearchV2Response,
} from 'app/modules/search/response';
import { convertV2Result } from 'app/modules/search/utils';

export const useGlobalSearch = (
  type: SearchTypes | undefined,
  payload: SearchRequest,
  newPayload: GenericSearchPayload,
) => {
  const isSearchV2Enabled = useSelector(selectSearchV2Enabled);
  const useDlHashId = useSelector(selectSearchV2UseDlHashIdEnabled);

  const isFlows = type === SearchTypes.FLOWS;

  const flowsQuery = useQuery<SearchResponse>({
    queryFn: () => post<FlowsSearchResponse>('/rules/flows/search', payload),
    queryKey: NAVIGATOR_QUERY_KEYS.search(type, payload),
    enabled: Boolean(isFlows && payload.phrase && payload.phrase.length > 1),
    staleTime: 5 * 60 * 1000,
  });

  const v1Query = useQuery<SearchResponse>({
    queryFn: () => {
      switch (type) {
        case SearchTypes.ACTION_EVENTS:
          return post<EventsSearchResponse>('/action-events/search', payload);
        case SearchTypes.ALERTS:
          return post<AlertsSearchResponse>('/alerts/search', payload);
        case SearchTypes.CASES:
          return post<CasesSearchResponse>('/cases/search', payload);
        case SearchTypes.ENTITIES:
          return post<EntitySearchResponse>('/entities/search', payload);
        case SearchTypes.INSTRUMENTS:
          return post<InstrumentsSearchResponse>(
            '/instruments/search',
            payload,
          );
        case SearchTypes.RULES:
          return post<RulesSearchResponse>('/rules/search', payload);
        case SearchTypes.TEAMS:
          return post<TeamsSearchResponse>('/teams/search', payload);
        case SearchTypes.TRANSACTIONS:
          return post<EventsSearchResponse>('/events/search', payload);
        default:
          return Promise.resolve({ data: [] });
      }
    },
    queryKey: NAVIGATOR_QUERY_KEYS.search(type, payload),
    enabled: Boolean(
      !isSearchV2Enabled &&
      !isFlows &&
      type &&
      payload.phrase &&
      payload.phrase.length > 1,
    ),
    staleTime: 5 * 60 * 1000,
  });

  const v2Query = useQuery<SearchV2Response, Error, SearchResponse>({
    queryFn: () => {
      switch (type) {
        case SearchTypes.ENTITIES:
          return post<EntitySearchV2Response>(
            '/entities/search/v2',
            newPayload,
          );
        case SearchTypes.TRANSACTIONS:
          return post<TransactionSearchV2Response>(
            '/txn_events/search/v2',
            newPayload,
          );
        case SearchTypes.ACTION_EVENTS:
          return post<ActionEventSearchV2Response>(
            '/action_events/search/v2',
            newPayload,
          );
        case SearchTypes.INSTRUMENTS:
          return post<InstrumentSearchV2Response>(
            '/instruments/search/v2',
            newPayload,
          );
        case SearchTypes.ALERTS:
          return post<AlertSearchV2Response>('/alerts/search/v2', newPayload);
        case SearchTypes.CASES:
          return post<CaseSearchV2Response>('/cases/search/v2', newPayload);
        case SearchTypes.RULES:
          return post<RuleSearchV2Response>('/rules/search/v2', newPayload);
        case SearchTypes.TEAMS:
          return post<TeamSearchV2Response>('/teams/search/v2', newPayload);
        case SearchTypes.NOTES:
          return post<NotesSearchV2Response>('/notes/search/v2', newPayload);
        default:
          return Promise.resolve({ hits: [], took: 0 });
      }
    },
    queryKey: NAVIGATOR_QUERY_KEYS.search(type, newPayload),
    enabled: Boolean(
      isSearchV2Enabled &&
      !isFlows &&
      type &&
      newPayload?.search_conditions?.some((c) =>
        c.phrases.some((p) => p.length > 1),
      ),
    ),
    staleTime: 5 * 60 * 1000,
    select: (data) => convertV2Result(data, type, useDlHashId),
  });

  if (isFlows) {
    return flowsQuery;
  }

  return isSearchV2Enabled ? v2Query : v1Query;
};
